import { useEffect } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useIsStarter } from '#src/app/shared/hooks/session';

import { type EntryDecision, resolveEntry } from './entryResolution';

/** Build the destination URL, stamping `started_flow=true` and consuming the one-shot `new_draft`. */
function buildEntryUrl(
	decision: Extract<EntryDecision, { kind: 'deeplink' | 'from_draft' }>,
	{ pathname, search }: { pathname: string; search: string },
): string {
	// Drop `new_draft` here — it's a one-shot entry signal, so it shouldn't ride along the flow.
	const { new_draft: newDraft, ...params } = qs.parse(search, { ignoreQueryPrefix: true });
	const query = qs.stringify({ ...params, started_flow: true });

	// honor the deeplink as-is, but stamp `started_flow=true` to mark it resolved for future renders.
	if (decision.kind === 'deeplink') return `${pathname}?${query}`;

	// for a draft-derived step, replace the last segment of the path with the resolved step.
	const pathWithoutStep = pathname.split('/').slice(0, -1).join('/');
	return `${pathWithoutStep}/${decision.step}?${query}`;
}

function formatUnknownForErrorReport(value: unknown): string {
	if (value instanceof Error) return value.message;
	if (typeof value === 'string') return value;
	return JSON.stringify(value);
}

export function useRedirectHandler() {
	const { data } = useStartAPetitionPageData();
	const { errorReporter } = useUtilityContext();
	const { pathname, search } = useLocation();
	const startedFlowParam = useQueryParamValue('started_flow');
	const sourceLocation = useQueryParamValue('source_location');
	const isNewDraft = useQueryParamValue('new_draft') === 'true';
	const isStarter = useIsStarter();

	const track = useTracking();
	const navigate = useNavigate();

	const decision = resolveEntry(startedFlowParam, data.draft, { isNewDraft });

	useEffect(() => {
		if (decision.kind === 'resolved') return;

		if (decision.kind === 'from_draft' && decision.parseException != null) {
			void errorReporter.report(
				{
					error: new Error('Failed to parse draft', { cause: decision.parseException }),
					// `feature` routes the Sentry issue to the owning product area.
					context: { feature: 'petition_creation' },
					// Only the two fields with demonstrated diagnostic value (which client re-fired, which
					// schema branch ran); the cause error's stack identifies the failure. No draft content
					// or shape/presence signals — the no-PII goal of this report.
					params: {
						operation: 'useRedirectHandler',
						draftId: data.draft.id,
						draftType: data.draft.type,
						parseError: formatUnknownForErrorReport(decision.parseException),
					},
				},
				{ severity: 'error' },
			);
		}

		// `sap_view` mirrors the entry decision: common properties always, plus the draft-axis
		// properties only when the draft decided the step (a deeplink's step is caller-chosen).
		const commonTrackingData = { entry_type: decision.kind, source_location: sourceLocation, is_starter: isStarter };
		void track('sap_view', {
			...commonTrackingData,
			...(decision.kind === 'from_draft' ? { draft_state: decision.draftState, landing_step: decision.step } : {}),
		});

		const entryUrl = buildEntryUrl(decision, { pathname, search });
		navigate(entryUrl, { replace: true, preventScrollReset: true });
	}, []);

	return { data: { isStepResolved: decision.kind === 'resolved' } };
}
